<template>
  <div class="mt-2 bg-white p-4 rounded-md">
    <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">Informations sur l'entreprise</p>
    <div class="grid grid-cols-2 gap-x-4 gap-y-3">
      <div class="flex-1">
        <label for="sirenNumber" class="block text-sm text-gray-700">
          SIREN
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          type="text"
          name="customer.sirenNumber"
          id="sirenNumber"
          autocomplete="given-name"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Numéro SIREN"
          v-model="localCustomer.sirenNumber"
          @keydown="blockEnterBehavior"
        />
        <ErrorMessage name="customer.sirenNumber" class="mt-2 text-sm text-red-600" />
      </div>

      <div class="flex-1">
        <label for="companyName" class="block text-sm text-gray-700">
          Nom de l'entreprise
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          type="text"
          name="customer.companyName"
          id="companyName"
          autocomplete="organization"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Estaly"
          v-model="localCustomer.companyName"
          @keydown="blockEnterBehavior"
        />
        <ErrorMessage name="customer.companyName" class="mt-2 text-sm text-red-600" />
      </div>

      <div class="flex-1">
        <label for="legalForm" class="block text-sm text-gray-700">
          Forme juridique
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          as="select"
          name="customer.legalForm"
          id="legalForm"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          v-model="localCustomer.legalForm"
        >
          <option value="">Sélectionner une forme juridique</option>
          <option value="sarl">SARL</option>
          <option value="eurl">EURL</option>
          <option value="sas">SAS</option>
          <option value="sasu">SASU</option>
          <option value="sa">SA</option>
          <option value="ei">EI</option>
          <option value="sca">SCA</option>
          <option value="scs">SCS</option>
          <option value="snc">SNC</option>
        </Field>
        <ErrorMessage name="customer.legalForm" class="mt-2 text-sm text-red-600" />
      </div>

      <!-- Address Fields -->
      <GoogleAddress class="flex-1" @newAddress="updateAddress" :addressError="addressError" />

      <!-- Buyer's fields -->
      <div class="flex-1">
        <label for="firstName" class="block text-sm text-gray-700">
          Prénom du représentant
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          type="text"
          name="customer.firstName"
          id="firstName"
          autocomplete="given-name"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Jean"
          v-model="localCustomer.firstName"
          @keydown="blockEnterBehavior"
        />
        <ErrorMessage name="customer.firstName" class="mt-2 text-sm text-red-600" />
      </div>

      <div class="flex-1">
        <label for="lastName" class="block text-sm text-gray-700">
          Nom du représentant
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          type="text"
          name="customer.lastName"
          id="lastName"
          autocomplete="family-name"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Dupont"
          v-model="localCustomer.lastName"
          @keydown="blockEnterBehavior"
        />
        <ErrorMessage name="customer.lastName" class="mt-2 text-sm text-red-600" />
      </div>

      <div class="flex-1">
        <label for="buyerRole" class="block text-sm text-gray-700">
          Fonction exercée par le représentant
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          type="text"
          name="customer.buyerRole"
          id="buyerRole"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="Directeur général"
          v-model="localCustomer.buyerRole"
          @keydown="blockEnterBehavior"
        />
        <ErrorMessage name="customer.buyerRole" class="mt-2 text-sm text-red-600" />
      </div>

      <div class="flex-1">
        <label for="email" class="block text-sm text-gray-700">
          Email
          <span class="ml-1 text-red-600">*</span>
        </label>
        <Field
          id="email"
          name="customer.email"
          type="email"
          class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
          placeholder="customer@gmail.com"
          v-model="localCustomer.email"
          @keydown="blockEnterBehavior"
        />
        <ErrorMessage name="customer.email" class="mt-2 text-sm text-red-600" />
      </div>

      <PhoneNumberFormField v-model="localCustomer.phone" :shipping-address="selectedStore?.shippingAddress" />
    </div>
  </div>
</template>

<script>
import { Field, ErrorMessage } from "vee-validate";
import GoogleAddress from "@/components/address/GoogleAddress";
import PhoneNumberFormField from "./customer_form/PhoneNumberFormField.vue";
import { mapGetters } from "vuex";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

export default {
  components: {
    Field,
    ErrorMessage,
    GoogleAddress,
    PhoneNumberFormField,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    city: {
      type: String,
      default: "",
    },
    addressError: {
      type: Boolean,
      default: false,
    },
  },
  inject: ["validationContext"],

  computed: {
    ...mapGetters("store", ["selectedStore"]),

    localCustomer: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  mounted() {
    // Validate all required fields for professional customer form
    this.validationContext?.validateField("customer.sirenNumber");
    this.validationContext?.validateField("customer.companyName");
    this.validationContext?.validateField("customer.legalForm");
    this.validationContext?.validateField("customer.firstName");
    this.validationContext?.validateField("customer.lastName");
    this.validationContext?.validateField("customer.buyerRole");
    this.validationContext?.validateField("customer.email");
    this.validationContext?.validateField("customer.phone");
  },
  methods: {
    blockEnterBehavior,
    updateAddress(value) {
      this.$emit("newAddress", value);
    },
  },
};
</script>
